/**
 * <ia-reco-slider> — fills any block of product cards with an AB Tasty
 * recommendation, configured through data-endpoint / data-strategy /
 * data-variables / data-limit. Serves the homepage and both PDP blocks; the
 * mini cart has its own wiring in ia-cart-upsell.js.
 *
 * It never empties the block: any failure leaves the server-rendered products
 * in place. See docs/abtasty-integration.md.
 */

import { readCached, writeCached, ttlFromAttribute } from 'ia-reco-cache';

// Two viewports early, so the swap has already happened by the time the
// shopper scrolls there and never watches products change in front of them.
const REVEAL_MARGIN = '200% 0px';
// Fallback for a slow first paint, so the block still prepares out of sight.
const IDLE_START_MS = 1500;
const FETCH_TIMEOUT_MS = 4000;
// The card markup differs by placement (standard card, cart upsell card, the
// search drawer's mini card), so the section is a seam: callers that need
// another card name it in data-card-section.
const CARD_SECTION = 'ia-reco-cards';
// A block with different markup opts in via data-reco-track / data-reco-slot.
const TRACK_SELECTOR = '[data-reco-track], .ia-slider__slides';
const SLOT_SELECTOR = '[data-reco-slot], .keen-slider__slide';
// Two ages, matching the cart. The picks are ids from the proxy and keep for
// hours; the cards are Shopify markup with prices in them and do not keep at
// all unless a merchant says otherwise.
const PICKS_TTL_MS = 12 * 60 * 60 * 1000;
const CARDS_TTL_MS = 0;

function fetchJson(url, signal) {
  return fetch(url, { signal }).then((res) => (res.ok ? res.json() : null));
}

class IaRecoSlider extends HTMLElement {
  connectedCallback() {
    const { endpoint, strategy } = this.dataset;
    if (!endpoint || !strategy) return;
    this.cardSection = this.dataset.cardSection || CARD_SECTION;

    // The wrapper is display:contents and has no box, so watch what it wraps.
    this.target = this.firstElementChild || this;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.start();
      },
      { rootMargin: REVEAL_MARGIN }
    );
    this.observer.observe(this.target);

    this.idleTimer = setTimeout(() => this.start(), IDLE_START_MS);
  }

  start() {
    if (this.started) return;
    this.started = true;
    this.observer?.disconnect();
    clearTimeout(this.idleTimer);
    this.hydrate();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    clearTimeout(this.idleTimer);
    this.controller?.abort();
  }

  /**
   * Where this block's two entries live. Everything that changes the answer is
   * in the signature, so a PDP for another product or a merchant's new strategy
   * misses rather than replays; the slot keeps two blocks on one page (homepage
   * strip, repurchase row) out of each other's entry.
   */
  get cacheKeys() {
    const { endpoint, strategy, variables, limit, source, customer } = this.dataset;
    const route = source === 'repurchase' ? 'repurchase' : 'recos';
    const base = `slider:${route}:${strategy}`;
    return {
      picksSlot: `${base}:picks`,
      cardsSlot: base,
      // The shopper is part of the signature: a repurchase list is built from
      // their own orders, and sessionStorage outlives a logout in the same tab.
      signature: [endpoint, route, strategy, variables || '', limit || '', customer || ''].join('|'),
      picksTtl: ttlFromAttribute(this.dataset.abtastyCacheTtl, PICKS_TTL_MS),
      cardsTtl: ttlFromAttribute(this.dataset.recoCardsTtl, CARDS_TTL_MS),
    };
  }

  async hydrate() {
    this.controller = new AbortController();
    const timer = setTimeout(() => this.controller.abort(), FETCH_TIMEOUT_MS);
    const { picksSlot, cardsSlot, signature, picksTtl, cardsTtl } = this.cacheKeys;

    try {
      // Rendered cards, when the merchant allows it: nothing is fetched at all.
      const cardsHtml = readCached(cardsSlot, signature, cardsTtl);
      if (cardsHtml) {
        const parser = document.createElement('div');
        parser.innerHTML = cardsHtml;
        const cards = [...parser.children];
        if (cards.length && this.isConnected) {
          this.render(cards);
          return;
        }
      }

      // The picks alone: the proxy is skipped, Shopify still draws the cards,
      // so prices and stock are today's.
      const cachedPicks = readCached(picksSlot, signature, picksTtl);
      const items = cachedPicks ? JSON.parse(cachedPicks) : await this.fetchRecommendations();
      if (!items?.length || !this.isConnected) return;
      if (!cachedPicks) writeCached(picksSlot, signature, picksTtl, JSON.stringify(items));

      const cards = await this.collectCards(items);
      if (cards.length && this.isConnected) {
        this.render(cards);
        writeCached(cardsSlot, signature, cardsTtl, cards.map((card) => card.outerHTML).join(''));
      }
    } catch {
      // Leave the server-rendered products in place.
    } finally {
      clearTimeout(timer);
      // Either way the block stops hiding: recommendation or fallback.
      this.removeAttribute('data-reco-pending');
    }
  }

  fetchRecommendations() {
    const { endpoint, strategy, variables, limit, source } = this.dataset;
    // Repurchase builds its context from the shopper's orders on the proxy
    // side, so the only difference here is which route answers.
    const route = source === 'repurchase' ? 'repurchase' : 'recos';
    const params = new URLSearchParams({ strategy });
    if (variables) params.set('variables', variables);
    if (limit) params.set('limit', limit);

    return fetchJson(
      `${endpoint.replace(/\/$/, '')}/${route}?${params}`,
      this.controller.signal
    ).then((data) => (Array.isArray(data?.items) ? data.items.filter((item) => item.handle) : null));
  }

  /** Reuses cards already on the page; fetches only the missing ones, in one request. */
  async collectCards(items) {
    const existing = new Map();
    this.querySelectorAll('[data-product-handle]').forEach((node) => {
      const slide = node.closest(SLOT_SELECTOR);
      if (!slide) return;
      const handle = node.dataset.productHandle;
      if (!existing.has(handle)) existing.set(handle, slide);
    });

    const missing = items.map((item) => item.handle).filter((handle) => !existing.has(handle));
    const fetched = missing.length ? await this.fetchCards(missing) : new Map();

    return items
      .map((item) => existing.get(item.handle) || fetched.get(item.handle))
      .filter(Boolean);
  }

  async fetchCards(handles) {
    const query = handles.map((handle) => `handle:${handle}`).join(' OR ');
    const url = `/search?type=product&q=${encodeURIComponent(query)}&section_id=${this.cardSection}`;
    const response = await fetch(url, { signal: this.controller.signal });
    if (!response.ok) return new Map();

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const rendered = new Map();
    doc.querySelectorAll('[data-reco-card]').forEach((card) => {
      rendered.set(card.dataset.productHandle, this.wrapCard(card));
    });
    return rendered;
  }

  /** Transplants a fetched card into a clone of the block's own slide. */
  wrapCard(card) {
    const template = this.querySelector(SLOT_SELECTOR);
    if (!template) return card;

    const slide = template.cloneNode(false);
    slide.innerHTML = card.innerHTML;
    slide.setAttribute('data-product-handle', card.dataset.productHandle);
    return slide;
  }

  render(cards) {
    const track = this.querySelector(TRACK_SELECTOR);
    if (!track) return;

    const fragment = document.createDocumentFragment();
    cards.forEach((card) => fragment.appendChild(card));

    // If the shopper is already looking at the block, fade rather than snap.
    const visible = this.target.getBoundingClientRect().top < window.innerHeight;
    if (visible) track.classList.add('is-reco-swapping');
    track.replaceChildren(fragment);
    if (visible) requestAnimationFrame(() => track.classList.remove('is-reco-swapping'));

    this.removeAttribute('data-reco-pending');

    // Keen measured the old slides, so rebuild it on the new ones.
    const slider = this.querySelector('ia-slider');
    if (slider?.initializeSlideshow) {
      slider.slider?.destroy();
      slider.initializeSlideshow();
    }
    // The keen instance is new: anything driving this slider from outside (the
    // tabs' shared arrows) has to re-bind.
    this.dispatchEvent(new CustomEvent('ia-reco:rendered', { bubbles: true }));
  }
}

if (!customElements.get('ia-reco-slider')) {
  customElements.define('ia-reco-slider', IaRecoSlider);
}
